import { getAudioPlayerElement, getCurrentAudioFile, getAudioPlayerCollapsed } from './player-core.js';

// Configuration
const COOKIE_POSITION_INTERVAL_MS = 3000; // Save position to cookie every 3 seconds
const DB_SAVE_INTERVAL_MS = 180000; // Save to DB every 3 minutes (180 seconds)
const COOKIE_NAME_POSITION = 'audio_player_playback_position';
const COOKIE_NAME_TRACK = 'audio_player_current_track';
const COOKIE_NAME_COLLAPSED = 'audio_player_collapsed';
const COOKIE_NAME_CLOSED = 'audio_player_closed'; // Flag to indicate player was explicitly closed
const SCOPE = 'audio_player';
const KEY_POSITION = 'position';
const KEY_CURRENT_TRACK = 'current_track';
const KEY_COLLAPSED = 'collapsed';

// State
let cookieSaveInterval = null;
let dbSaveInterval = null;
let lastSavedPosition = null;
let lastSavedTrack = null;
let lastSavedCollapsed = null;
let isInitialized = false;

// Cookie helpers
function saveToCookie(name, value) {
	try {
		const expires = new Date();
		expires.setFullYear(expires.getFullYear() + 1);
		document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))}; expires=${expires.toUTCString()}; path=/`;
	} catch (e) {
		console.warn('[AudioPlayerState] Failed to save to cookie:', e);
	}
}

function loadFromCookie(name) {
	try {
		const cookies = document.cookie.split(';');
		for (let cookie of cookies) {
			cookie = cookie.trim();
			if (cookie.startsWith(name + '=')) {
				const value = decodeURIComponent(cookie.substring(name.length + 1));
				// If cookie value is empty or just whitespace, treat as not found
				if (!value || value.trim() === '') {
					return null;
				}
				return JSON.parse(value);
			}
		}
	} catch (e) {
		console.warn('[AudioPlayerState] Failed to load from cookie:', e);
	}
	return null;
}

// Get current playback state
function getCurrentPlaybackState() {
	const audioPlayerElement = getAudioPlayerElement();
	if (!audioPlayerElement || !audioPlayerElement.length) {
		return null;
	}
	
	const audio = audioPlayerElement.find('audio');
	if (audio.length === 0 || !audio[0]) {
		return null;
	}
	
	const audioEl = audio[0];
	const currentFile = getCurrentAudioFile();
	const collapsed = getAudioPlayerCollapsed();
	
	return {
		position: audioEl.currentTime || 0,
		duration: audioEl.duration || 0,
		collapsed: collapsed || false,
		track: currentFile ? {
			fileKey: currentFile.fileKey,
			path: currentFile.file?.p || currentFile.file?.path || '',
			name: currentFile.file?.n || currentFile.file?.name || ''
		} : null
	};
}

// Save position to cookie (fast, frequent)
function savePositionToCookie() {
	const state = getCurrentPlaybackState();
	if (!state || !state.track) return;
	
	const positionData = {
		position: state.position,
		duration: state.duration,
		timestamp: Date.now()
	};
	
	// Only save if position changed significantly (> 0.5 seconds)
	if (lastSavedPosition === null || Math.abs(lastSavedPosition - state.position) > 0.5) {
		saveToCookie(COOKIE_NAME_POSITION, positionData);
		lastSavedPosition = state.position;
	}
	
	// Save track info
	const trackData = {
		fileKey: state.track.fileKey,
		path: state.track.path,
		name: state.track.name,
		timestamp: Date.now()
	};
	
	if (!lastSavedTrack || lastSavedTrack.fileKey !== trackData.fileKey) {
		saveToCookie(COOKIE_NAME_TRACK, trackData);
		lastSavedTrack = trackData;
	}
	
	// Save collapsed state
	const collapsedData = {
		collapsed: state.collapsed || false,
		timestamp: Date.now()
	};
	saveToCookie(COOKIE_NAME_COLLAPSED, collapsedData);
}

// Save to database via UserSettings API (slower, less frequent)
// This function reads from cookies and syncs to database (cookies are the source of truth)
function saveToDatabase() {
	// Read current state from cookies (cookies are the source of truth)
	const cookieTrack = loadFromCookie(COOKIE_NAME_TRACK);
	const cookiePosition = loadFromCookie(COOKIE_NAME_POSITION);
	const cookieCollapsed = loadFromCookie(COOKIE_NAME_COLLAPSED);
	
	// If no track in cookies, nothing to save
	if (!cookieTrack || !cookieTrack.fileKey) {
		return;
	}
	
	// Check if UserSettings API is available
	if (!window.SL || !window.SL.UserSettings) {
		return; // API not available, skip DB save
	}
	
	// Check if anything actually changed - compare with last saved values
	const positionChanged = lastSavedPosition === null || 
		!cookiePosition || 
		Math.abs(lastSavedPosition - cookiePosition.position) > 0.5;
	const trackChanged = lastSavedTrack === null || 
		!lastSavedTrack.fileKey || 
		lastSavedTrack.fileKey !== cookieTrack.fileKey;
	const collapsedChanged = lastSavedCollapsed === null || 
		!cookieCollapsed ||
		lastSavedCollapsed !== cookieCollapsed.collapsed;
	
	// If nothing changed, skip DB save
	if (!positionChanged && !trackChanged && !collapsedChanged) {
		return;
	}
	
	const items = [];
	
	// Save current track (only if changed)
	if (trackChanged) {
		items.push({
			entity_type: null,
			entity_id: null,
			key: KEY_CURRENT_TRACK,
			value: {
				fileKey: cookieTrack.fileKey,
				path: cookieTrack.path,
				name: cookieTrack.name,
				updatedAt: cookieTrack.timestamp || Date.now()
			}
		});
		lastSavedTrack = {
			fileKey: cookieTrack.fileKey,
			path: cookieTrack.path,
			name: cookieTrack.name
		};
	}
	
	// Save position (only if changed and > 1 second)
	if (positionChanged && cookiePosition && cookiePosition.position > 1) {
		items.push({
			entity_type: null,
			entity_id: null,
			key: KEY_POSITION,
			value: {
				position: cookiePosition.position,
				duration: cookiePosition.duration || 0,
				updatedAt: cookiePosition.timestamp || Date.now()
			}
		});
		lastSavedPosition = cookiePosition.position;
	}
	
	// Save collapsed state (only if changed)
	if (collapsedChanged && cookieCollapsed) {
		items.push({
			entity_type: null,
			entity_id: null,
			key: KEY_COLLAPSED,
			value: {
				collapsed: cookieCollapsed.collapsed || false,
				updatedAt: cookieCollapsed.timestamp || Date.now()
			}
		});
		lastSavedCollapsed = cookieCollapsed.collapsed;
	}
	
	if (items.length > 0) {
		// Use bulk save via direct API call
		if (window.$ && window.$.ajax) {
			window.$.ajax({
				url: '/api/user-settings',
				method: 'PUT',
				data: JSON.stringify({
					scope: SCOPE,
					items: items
				}),
				contentType: 'application/json',
				dataType: 'json'
			}).then(() => {
				// Database synced from cookies
			}).catch(err => {
				console.warn('[AudioPlayerState] Failed to save to database:', err);
			});
		}
	}
}

// Load saved state (synchronous - returns immediately, uses cookies as fallback)
export function loadSavedState() {
	const savedState = {
		track: null,
		position: null,
		collapsed: false
	};
	
	// Check if player was explicitly closed - if so, don't restore
	const wasClosed = loadFromCookie(COOKIE_NAME_CLOSED);
	if (wasClosed && wasClosed.closed === true) {
		return savedState;
	}
	
	// Load from cookies first (fast, always available)
	const cookieTrack = loadFromCookie(COOKIE_NAME_TRACK);
	const cookiePosition = loadFromCookie(COOKIE_NAME_POSITION);
	const cookieCollapsed = loadFromCookie(COOKIE_NAME_COLLAPSED);
	
	if (cookieTrack && cookieTrack.fileKey) {
		savedState.track = cookieTrack;
	}
	if (cookiePosition && typeof cookiePosition.position === 'number') {
		savedState.position = cookiePosition.position;
	}
	if (cookieCollapsed && typeof cookieCollapsed.collapsed === 'boolean') {
		savedState.collapsed = cookieCollapsed.collapsed;
	}
	
	// If no track found in cookies, don't try to load from DB (state was cleared)
	if (!savedState.track || !savedState.track.fileKey) {
		return savedState;
	}
	
	// Try to load from database (async, will update if available)
	// This is best-effort and doesn't block
	if (window.SL && window.SL.UserSettings) {
		window.SL.UserSettings.getScope({
			scope: SCOPE,
			entityType: null,
			entityId: null
		}).then(settings => {
			if (settings && settings[KEY_CURRENT_TRACK]) {
				// Update saved state if DB has newer data
				savedState.track = settings[KEY_CURRENT_TRACK];
			}
			if (settings && settings[KEY_POSITION]) {
				// Update saved state if DB has newer data
				savedState.position = settings[KEY_POSITION].position;
			}
			if (settings && settings[KEY_COLLAPSED]) {
				// Update saved state if DB has newer data
				savedState.collapsed = settings[KEY_COLLAPSED].collapsed || false;
			}
		}).catch(err => {
			console.warn('[AudioPlayerState] Failed to load from database:', err);
		});
	}
	
	return savedState;
}

// Initialize state persistence
export function initPlayerStatePersistence() {
	if (isInitialized) return;
	isInitialized = true;
	
	const audioPlayerElement = getAudioPlayerElement();
	if (!audioPlayerElement || !audioPlayerElement.length) return;
	
	const audio = audioPlayerElement.find('audio');
	if (audio.length === 0 || !audio[0]) return;
	
	const audioEl = audio[0];
	
	// Save position to cookie frequently (every 3 seconds)
	cookieSaveInterval = setInterval(() => {
		savePositionToCookie();
	}, COOKIE_POSITION_INTERVAL_MS);
	
	// Save to database less frequently (every 3 minutes)
	dbSaveInterval = setInterval(() => {
		saveToDatabase();
	}, DB_SAVE_INTERVAL_MS);
	
	// Save to cookies on pause/seek events (but NOT to database immediately)
	audioEl.addEventListener('pause', () => {
		savePositionToCookie(); // Save to cookies immediately
		// Don't save to DB immediately - let interval handle it
	});
	
	audioEl.addEventListener('seeked', () => {
		savePositionToCookie(); // Save to cookies immediately
		// Don't save to DB immediately - let interval handle it
	});
	
	// Watch for collapsed state changes
	// Re-check audioPlayerElement in case it was created after init
	if (audioPlayerElement && audioPlayerElement.length) {
		const observer = new MutationObserver(() => {
			// Save to cookies when collapsed state changes (but NOT to database immediately)
			savePositionToCookie();
			// Don't save to DB immediately - let interval handle it
		});
		observer.observe(audioPlayerElement[0], {
			attributes: true,
			attributeFilter: ['class']
		});
	}
	
	// Save on page unload (beforeunload/pagehide) - this is the only immediate DB save
	const saveOnUnload = () => {
		savePositionToCookie();
		saveToDatabase(); // Save to DB on unload to ensure state is persisted
	};
	
	window.addEventListener('beforeunload', saveOnUnload);
	window.addEventListener('pagehide', saveOnUnload);
	
	// Cleanup function
	return () => {
		if (cookieSaveInterval) {
			clearInterval(cookieSaveInterval);
			cookieSaveInterval = null;
		}
		if (dbSaveInterval) {
			clearInterval(dbSaveInterval);
			dbSaveInterval = null;
		}
		window.removeEventListener('beforeunload', saveOnUnload);
		window.removeEventListener('pagehide', saveOnUnload);
	};
}

// Cleanup state persistence
export function cleanupPlayerStatePersistence() {
	if (cookieSaveInterval) {
		clearInterval(cookieSaveInterval);
		cookieSaveInterval = null;
	}
	if (dbSaveInterval) {
		clearInterval(dbSaveInterval);
		dbSaveInterval = null;
	}
	isInitialized = false;
}

// Clear saved state (cookies and DB) - called when player is closed
export function clearSavedState() {
	// Set "closed" flag first to prevent restoration
	try {
		const expires = new Date();
		expires.setFullYear(expires.getFullYear() + 1);
		saveToCookie(COOKIE_NAME_CLOSED, { closed: true });
	} catch (e) {
		console.warn('[AudioPlayerState] Failed to set closed flag:', e);
	}
	
	// Clear cookies - set empty value with expired date
	try {
		const expires = new Date(0); // Expire immediately (epoch time)
		const cookieOptions = `expires=${expires.toUTCString()}; path=/`;
		
		// Clear cookies by setting empty value with expired date
		document.cookie = `${COOKIE_NAME_POSITION}=; ${cookieOptions}`;
		document.cookie = `${COOKIE_NAME_TRACK}=; ${cookieOptions}`;
		document.cookie = `${COOKIE_NAME_COLLAPSED}=; ${cookieOptions}`;
		
		// Also try to clear with SameSite=None; Secure for cross-site scenarios
		document.cookie = `${COOKIE_NAME_POSITION}=; ${cookieOptions}; SameSite=None; Secure`;
		document.cookie = `${COOKIE_NAME_TRACK}=; ${cookieOptions}; SameSite=None; Secure`;
		document.cookie = `${COOKIE_NAME_COLLAPSED}=; ${cookieOptions}; SameSite=None; Secure`;
		
		// Verify cookies are cleared
	} catch (e) {
		console.warn('[AudioPlayerState] Failed to clear cookies:', e);
	}
	
	// Clear from database via DELETE endpoint
	if (window.$ && window.$.ajax) {
		const items = [
			{
				entity_type: null,
				entity_id: null,
				key: KEY_CURRENT_TRACK
			},
			{
				entity_type: null,
				entity_id: null,
				key: KEY_POSITION
			},
			{
				entity_type: null,
				entity_id: null,
				key: KEY_COLLAPSED
			}
		];
		
		window.$.ajax({
			url: '/api/user-settings',
			method: 'DELETE',
			data: JSON.stringify({
				scope: SCOPE,
				items: items
			}),
			contentType: 'application/json',
			dataType: 'json'
		}).then(() => {
			// Database cleared via DELETE
		}).catch(err => {
			console.warn('[AudioPlayerState] Failed to clear database:', err);
		});
	}
	
	// Reset tracking variables
	lastSavedPosition = null;
	lastSavedTrack = null;
	lastSavedCollapsed = null;
}

